import React from 'react';
import { filterDisabledTargets } from '../../utils/helpers';
import get from 'lodash/get';

// Custom recursive tree, replacing react-treebeard (unmaintained — pulled in
// velocity-react/ReactDOM.findDOMNode and an old react-transition-group, both
// removed in React 19). The tree is fully controlled by redux: each node carries
// `toggled` (expanded) and `active` (selected); a single `onToggle(node, toggled)`
// handler drives selection/expansion (see Target.onNodeToggle). Styles are ported
// from the previous react-treebeard THEME to keep the look unchanged.
const styles: Record<string, React.CSSProperties> = {
  tree: {
    listStyle: 'none',
    backgroundColor: 'transparent',
    margin: 0,
    padding: 0,
    color: 'black',
    fontSize: '16px',
    fontFamily: 'sans-serif'
  },
  subtree: {
    listStyle: 'none',
    paddingLeft: '19px',
    margin: 0
  },
  nodeBase: {
    position: 'relative'
  },
  link: {
    cursor: 'pointer',
    position: 'relative',
    padding: '5px',
    display: 'flex',
    alignItems: 'flex-start'
  },
  activeLink: {
    background: 'rgba(76, 169, 47, 1)', /* #4ca92f */
    borderRadius: '3px',
    color: '#ffffff'
  },
  toggle: {
    flex: '0 0 14px',
    marginLeft: '-5px',
    marginRight: '5px',
    textAlign: 'center',
    lineHeight: '24px'
  },
  header: {
    lineHeight: '24px',
    wordBreak: 'break-word'
  }
};

// A node of the target tree as the renderer needs it. Loose by design — this
// mirrors helpers.TreeTarget, which is deliberately server-shaped.
export interface TreeNodeData {
  _id?: string;
  name?: string;
  toggled?: boolean;
  active?: boolean;
  children?: TreeNodeData[];
  [key: string]: any;
}

type ToggleHandler = (node: TreeNodeData, toggled: boolean) => void;

const TreeNode = ({ node, onToggle }: { node: TreeNodeData; onToggle: ToggleHandler }) => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isEnabled = get(node, 'extra.c.enabled', true) === true;

  const linkStyle = node.active ? { ...styles.link, ...styles.activeLink } : styles.link;
  const headerStyle = isEnabled ? styles.header : { ...styles.header, opacity: 0.5 };

  // Matches react-treebeard: clicking a node fires onToggle with the *new*
  // toggled state; the redux-driven handler does selection + expansion.
  const handleToggle = () => onToggle(node, !node.toggled);

  let toggleIcon = '';
  if (hasChildren) {
    toggleIcon = node.toggled ? '▼' : '▶';
  }

  return (
    <li style={styles.nodeBase}>
      <div style={linkStyle} onClick={handleToggle}>
        <span style={styles.toggle}>{toggleIcon}</span>
        <span style={headerStyle}>{node.name}</span>
      </div>
      {hasChildren && node.toggled && (
        <ul style={styles.subtree}>
          {node.children!.map((child) => (
            <TreeNode key={child._id || child.name} node={child} onToggle={onToggle} />
          ))}
        </ul>
      )}
    </li>
  );
};

interface NodeTreeProps {
  data: TreeNodeData;
  showDisabledTargets: boolean;
  onToggle: ToggleHandler;
  className?: string;
}

const NodeTree = ({ data, showDisabledTargets, onToggle, className }: NodeTreeProps) => {
  // NOTE: this MUTATES `data` in place, during render. That is why Target.js
  // hands it a cloneDeep(tree) — and why that clone is a perf suspect on large
  // trees (see the target-editor-slow-node-select notes). Pruning into a new
  // structure instead would remove the need for the clone entirely.
  //
  // It also runs BEFORE the emptiness check below. Harmless today only because
  // the reducer initialises `tree` to {} rather than undefined, so
  // filterDisabledTargets always gets a real object.
  if (!showDisabledTargets) {
    // Cast: helpers' TreeTarget requires `_id`, but the reducer's initial tree
    // is `{}` — which is exactly the case the emptiness check below exists for.
    // filterDisabledTargets only reads `children`, so this is safe.
    filterDisabledTargets(data as any);
  }

  if (!data) {
    return <div className={className} />;
  }

  return (
    <div className={className}>
      <ul style={styles.tree}>
        <TreeNode node={data} onToggle={onToggle} />
      </ul>
    </div>
  );
};

export default React.memo(NodeTree);
